<template>
  <v-modal
    :id="id"
    :backdrop-static="true"
    size="lg"
    @closed="$emit('closed')"
  >
    <modal-header>
      <div class="d-flex align-items-center w-100 justify-content-between">
        <div class="d-flex align-items-center">
          <v-icon icon="eye" class="text-primary me-8" size="xl" />
          <span class="modal-title h5 mb-0">{{ activityDetails?.name || $t('template.previewTitle') }}</span>
        </div>
      </div>
    </modal-header>
    <modal-body style="max-height: 60vh; overflow-y: auto;">
      <div v-if="loading" class="d-flex justify-content-center align-items-center py-32">
        <v-loader />
      </div>
      <div v-else>
        <table class="modalTable w-100 mb-3">
          <tbody>
            <tr>
              <th>{{ $t('template.travelType') }}</th>
              <td>{{ travelTypeConcept }}</td>
            </tr>
            <tr>
              <th>{{ $t('general.shared.origin') }}</th>
              <td>{{ activityDetails?.origin?.name || '-' }}</td>
            </tr>
            <tr>
              <th>{{ $t('general.shared.destination') }}</th>
              <td>{{ activityDetails?.destination?.name || '-' }}</td>
            </tr>
            <tr>
              <th>{{ $t('general.shared.published') }}</th>
              <td>{{ activityDetails?.isPublished ? $t('general.button.yes') : $t('general.button.no') }}</td>
            </tr>
            <tr v-if="activityDetails?.complementaryText">
              <th>{{ $t('template.complementaryText') }}</th>
              <td>{{ activityDetails.complementaryText }}</td>
            </tr>
            <tr v-if="activityDetails?.termsConditionsText">
              <th>{{ $t('template.termsConditionsText') }}</th>
              <td>{{ activityDetails.termsConditionsText }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="activityDetails?.featuredImage && activityDetails.featuredImage.storageUrl" class="mb-3">
          <img :src="activityDetails.featuredImage.storageUrl" :alt="activityDetails.name" class="rounded" style="max-width: 120px; max-height: 120px;" />
        </div>
        <div v-if="days && days.length">
          <h6 class="fw-bold mt-4 mb-2">{{ $t('template.days') }}</h6>
          <div class="table-responsive">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{{ $t('general.shared.origin') }}</th>
                  <th>{{ $t('general.shared.destination') }}</th>
                  <th>{{ $t('general.shared.items') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(day, idx) in days" :key="day.id">
                  <td>{{ idx + 1 }}</td>
                  <td>{{ locations[day.originId] || '-' }}</td>
                  <td>{{ locations[day.destinationId] || '-' }}</td>
                  <td>{{ day.activityDayItems?.length || 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </modal-body>
    <modal-footer>
      <v-button v-if="activityDetails" variant="primary" class="ms-8" @click="goToActivity">
        <v-icon icon="arrow-right" class="me-8" />
        {{ $t('template.goToActivity') }}
      </v-button>
    </modal-footer>
  </v-modal>
</template>

<script lang="ts" setup>
import { defineProps, defineEmits, computed, ref, watch, onMounted } from 'vue';
import VModal from '@/components/vendor/basic/modal/VModal.vue';
import ModalHeader from '@/components/vendor/basic/modal/ModalHeader.vue';
import ModalBody from '@/components/vendor/basic/modal/ModalBody.vue';
import ModalFooter from '@/components/vendor/basic/modal/ModalFooter.vue';
import VButton from '@/components/vendor/basic/button/VButton.vue';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import VLoader from '@/components/vendor/loader/VLoader.vue';
import { useRouter } from 'vue-router';
import api from '@/api';
import { findConceptByKey } from '@/helpers/ConceptHelper';

const props = defineProps({
  agency: {
    type: String,
    required: true,
  },
  activityId: {
    type: [String, Number],
    required: true,
  },
  id: {
    type: String,
    default: 'template-preview-modal',
  },
});

defineEmits(['closed']);

const router = useRouter();
const loading = ref(true);
const activityDetails = ref<any>(null);
const days = ref<any[]>([]);
const locations = ref<Record<number, string>>({});

const travelTypeConcept = computed(() => {
  if (!activityDetails.value) return '-';
  const result = findConceptByKey('tour.travel_type', activityDetails.value.travelType);
  return typeof result === 'object' && result !== null && 'name' in result ? result.name : '-';
});

const fetchData = async () => {
  loading.value = true;
  try {
    const [activity, daysData] = await Promise.all([
      api.activityService.retrieveActivity(props.agency, Number(props.activityId)),
      api.activityService.retrieveActivityDays(props.agency, Number(props.activityId)),
    ]);
    activityDetails.value = activity;
    days.value = Array.isArray(daysData) ? daysData : (daysData ? [daysData] : []);

    // Fetch all locations and build a map
    const allLocations = await api.location.all();
    const locationMap: Record<number, string> = {};
    allLocations.forEach((loc: any) => {
      locationMap[loc.id] = loc.name || '-';
    });
    locations.value = locationMap;
  } catch (e) {
    activityDetails.value = null;
    days.value = [];
    locations.value = {};
  } finally {
    loading.value = false;
  }
};

watch(() => [props.agency, props.activityId], fetchData, { immediate: true });

function goToActivity() {
  router.push({
    name: 'activities.show',
    params: { activitiesId: props.activityId },
  });
}
</script> 